import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Toaster } from "./components/ui/sonner";
import LoginPage from "./components/LoginPage";
import HomePage from "./pages/HomePage";
import AccountSetup from "./pages/AccountSetup";
import ActivateAccount from "./pages/ActivateAccount";
import UserManagement from "./pages/UserManagement";
import DropDrive from "./pages/DropDrive";
import BulkSms from "./pages/BulkSms";
import JarvisIntegration from "./pages/JarvisIntegration";
import JarvisChief from "./pages/JarvisChief";
import JarvisPopup from "./components/jarvis/JarvisPopup";
import ChatLauncher from "./components/jarvis/chat/ChatLauncher";
import MyWeek from "./pages/MyWeek";
import BrokerProfileSettings from "./pages/BrokerProfileSettings";
import Calculator from "./components/Calculator";
import LVRCalculator from "./pages/LVRCalculator";
import HomeLoanCalculator from "./pages/HomeLoanCalculator";
import LMICalculator from "./pages/LMICalculator";
import LMIvsWaitCalculator from "./pages/LMIvsWaitCalculator";
import DischargesPage from "./pages/DischargesPage";
import ConstructionCostCalculator from "./pages/ConstructionCostCalculator";
// ActivityTracker is now embedded inside Game Plan Tracker → Metrics tab.
// The /activity URL redirects to /game-plan-tracker for backwards compatibility.
import HotOffThePress from "./pages/HotOffThePress";
import InvestorPropertyReport from "./pages/InvestorPropertyReport";
import BlueprintBreakDown from "./pages/BlueprintBreakDown";
import HeyDerwent from "./pages/HeyDerwent";  // eslint-disable-line no-unused-vars -- legacy page, route now redirects to /myweek

import BDMContactsPage from "./pages/BDMContactsPage";
import CompliancePage from "./pages/CompliancePage";
import NotesAI from "./pages/NotesAI";
import BreakDownReport from "./pages/BreakDownReport";
import AdminDashboard from "./pages/AdminDashboard";
import ClientPortal from "./pages/ClientPortal";
import ClientManagement from "./pages/ClientManagement";
import ClientPortalTemplates from "./pages/ClientPortalTemplates";
import ClientPortalReports from "./pages/ClientPortalReports";
import GamePlanTracker from "./pages/GamePlanTracker";
import GamePlanTV from "./pages/GamePlanTV";
import CFO from "./pages/CFO";
import CEO from "./pages/CEO";
import LenderSubmissionPacks from "./pages/LenderSubmissionPacks";
import LeadNurture from "./pages/LeadNurture";
import LeadNurtureLegacy from "./pages/LeadNurtureLegacy";
import PdfStudio from "./pages/PdfStudio";
import Work from "./pages/Work";
import MediaEngine from "./pages/MediaEngine";
import MarketingCommandCentre from "./pages/MarketingCommandCentre";
import MetaAdsTracker from "./pages/MetaAdsTracker";
import PreBuiltScripts from "./pages/PreBuiltScripts";
import SettledLoans from "./pages/SettledLoans";
import LeadDetailPage from "./pages/LeadDetailPage";
import CallsToText from "./pages/CallsToText";
import ReadMyIncome from "./pages/ReadMyIncome";
import Meetings from "./pages/Meetings";
import BrokerMeetingPage from "./pages/BrokerMeetingPage";
import PublicJoinPage from "./pages/PublicJoinPage";
import PublicRevisitPage from "./pages/PublicRevisitPage";
import WebRTCDiagnostics from "./pages/WebRTCDiagnostics";
import DemoBanner from "./components/DemoBanner";
import TodoList from "./pages/TodoList";
import StrategySessionPage from "./pages/StrategySessionPage";

// Bare-path redirect: /strategy-session → /strategy-session/. Uses a full
// page navigation (not Router Navigate) so the request leaves the SPA and
// hits the static asset host, which serves the A/B splitter's index.html.
const StrategySessionRedirect = () => {
  React.useEffect(() => {
    window.location.replace('/strategy-session/' + (window.location.search || '') + (window.location.hash || ''));
  }, []);
  return null;
};
import DerwentLinksPage from "./pages/DerwentLinksPage";
import DerwentLinksAdmin from "./pages/DerwentLinksAdmin";
import LeadsViewerHub from "./pages/LeadsViewerHub";
import IDVerificationAdmin from "./pages/IDVerificationAdmin";import IDVerifyCustomerPortal from "./pages/IDVerifyCustomerPortal";import IDVerifyStart from "./pages/IDVerifyStart";
import MyDerwentHub from "./pages/MyDerwentHub";
import DerwentHubAdmin from "./pages/DerwentHubAdmin";
import OnboardingPortal from "./pages/OnboardingPortal";
import PortalPreview from "./pages/PortalPreview";
import OnboardingWorkbench from "./pages/OnboardingWorkbench";
import CallingQueue from "./pages/CallingQueue";
import Contacts from "./pages/Contacts";
import CallHandoff from "./pages/CallHandoff";
import Webinars from "./pages/Webinars";
import WebinarStudio from "./pages/WebinarStudio";
import WebinarPublic from "./pages/WebinarPublic";
import WebinarRoom from "./pages/WebinarRoom";
import DerwentAIReports from "./pages/DerwentAIReports";
import SecurityCentre from "./pages/SecurityCentre";
import BrokerSetup from "./pages/BrokerSetup";
import OnboardingDetail from "./pages/OnboardingDetail";
import SystemDiagnostics from "./pages/SystemDiagnostics";
import FactFindSettings from "./pages/FactFindSettings";
import TeamTemplates from "./pages/TeamTemplates";
import DisclosureDocEditor from "./pages/DisclosureDocEditor";
import ExecutiveGrowthTool from "./pages/ExecutiveGrowthTool";
import FileReadinessRules from "./pages/FileReadinessRules";
import EsignDocumentDetail from "./pages/EsignDocumentDetail";
import EsignSigner from "./pages/EsignSigner";
import PostSettlementCRM from "./pages/PostSettlementCRM";
import PostSettlementClientProfile from "./pages/PostSettlementClientProfile";
import Workforce from "./pages/Workforce";
import MyActivity from "./pages/MyActivity";
import { WorkforceProvider } from "./components/workforce/WorkforceContext";
import { WarmThemeProvider } from "./theme/ThemeContext";
import InactivityMonitor from "./components/InactivityMonitor";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import useIsMobile from "./hooks/useIsMobile";
import { setLinkedWs } from "./lib/workspaceLink";

// Protected Route component
// A special-purpose "leads_viewer" role can only see two internal pages
// (strategy leads + derwent-links admin). Everything else — including the
// generic home page — redirects them to `/leads-viewer-hub`, a tiny landing
// screen that offers those two links + a Sign Out button.
const LEADS_VIEWER_ALLOWED = [
  '/account-setup',
  '/leads-viewer-hub',
  '/derwent-links-admin',
  '/login',
];
const LEADS_VIEWER_HOME = '/leads-viewer-hub';

// A restricted "broker_support" role (Emmanuel's onboarding assistants) can only
// use the Client Onboarding section. Everything else redirects to /onboarding.
// Enforced here in the SPA and again server-side via RESTRICTED_ROLE_PREFIXES.
const BROKER_SUPPORT_ALLOWED = [
  '/',
  '/account-setup',
  '/settings/broker-profile',
  '/onboarding',
  '/client-portal-team-templates',
  '/esign',
  '/my-activity',
  '/login',
  // View-only Game Plan Tracker (Emmanuel's book) — writes blocked server-side
  '/game-plan',
  // Broker Resources (excl. Integrations)
  '/discharges',
  '/hot-off-the-press',
  '/bdm-contacts',
  '/compliance',
  // Calculators
  '/accelerate-calculator',
  '/home-loan-calculator',
  '/lmi-calculator',
  '/lmi-vs-wait-calculator',
  '/lvr-calculator',
  '/construction-cost-calculator',
];
const BROKER_SUPPORT_HOME = '/onboarding';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Remember the deep link (e.g. a shared deal URL) so login lands on it.
    const target = `${location.pathname}${location.search}`;
    if (target !== '/' && target !== '/login') sessionStorage.setItem('post_login_redirect', target);
    return <Navigate to="/login" replace />;
  }

  // Mandatory first-login Account Setup: nothing else is reachable until every step is complete
  if (user?.setup_required && location.pathname.toLowerCase() !== '/account-setup') {
    return <Navigate to="/account-setup" replace />;
  }

  // Leads-viewer role: only allowed to see the hub + its two pages
  if (user?.role === 'leads_viewer') {
    const path = location.pathname.toLowerCase();
    const allowed = LEADS_VIEWER_ALLOWED.some((p) => path === p || path.startsWith(p + '/'));
    if (!allowed) return <Navigate to={LEADS_VIEWER_HOME} replace />;
  }

  // Broker-support role: only the Client Onboarding section; everything else → /onboarding
  if (user?.role === 'broker_support') {
    const path = location.pathname.toLowerCase();
    const allowed = BROKER_SUPPORT_ALLOWED.some((p) => path === p || path.startsWith(p + '/'));
    if (!allowed) return <Navigate to={BROKER_SUPPORT_HOME} replace />;
  }

  return children;
};

// After login, return to the deep link that triggered the login prompt (internal paths only).
// Home-screen app installs (old start_url /hey-derwent) should open on Home, not MyWeek.
const launchedAsApp = typeof window !== "undefined" && (window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator?.standalone === true);

const peekPostLoginRedirect = () => {
  const stored = sessionStorage.getItem('post_login_redirect');
  return stored && stored.startsWith('/') && !stored.startsWith('//') ? stored : '/';
};
const PostLoginRedirect = () => {
  const [target] = useState(peekPostLoginRedirect);
  useEffect(() => { sessionStorage.removeItem('post_login_redirect'); }, []);
  return <Navigate to={target} replace />;
};

// Public paths that must render instantly without waiting for the auth check
const FAST_PUBLIC_PATHS = ['/derwentlinks', '/links', '/strategy-session'];

// Main App Routes
const AppRoutes = () => {
  const { isAuthenticated, loading } = useAuth();
  const routeLocation = useLocation();
  const isPhone = useIsMobile();
  // A shared link into another broker's file only switches workspace for that file page.
  useEffect(() => { if (!/^\/onboarding\/[^/]+/.test(routeLocation.pathname)) setLinkedWs(''); }, [routeLocation.pathname]);
  // Customer-facing screens (fact find, uploads, signing, builder preview) never show internal widgets
  const isCustomerPortal = /^\/(onboard\/|esign\/sign\/|portal-preview|w\/)/i.test(routeLocation.pathname);
  const isFastPublic = FAST_PUBLIC_PATHS.includes(window.location.pathname.toLowerCase());

  if (loading && !isFastPublic) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Checking authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <WarmThemeProvider>
    <WorkforceProvider>
      <InactivityMonitor />
      {isAuthenticated && !isCustomerPortal && <DemoBanner />}
      {isAuthenticated && !isCustomerPortal && !isPhone && <PWAInstallPrompt />}
      {isAuthenticated && !isCustomerPortal && !isPhone && <JarvisPopup />}
      {isAuthenticated && !isCustomerPortal && <ChatLauncher />}
      <Routes>
      {/* Public Routes */}
      <Route 
        path="/login" 
        element={isAuthenticated ? <PostLoginRedirect /> : <LoginPage />} 
      />
      <Route path="/client-portal" element={<ClientPortal />} />
      <Route path="/w/:slug" element={<WebinarPublic />} />
      <Route path="/w/:slug/live" element={<WebinarRoom />} />
      <Route path="/webinars" element={<ProtectedRoute><Webinars /></ProtectedRoute>} />
      <Route path="/webinars/:id" element={<ProtectedRoute><WebinarStudio /></ProtectedRoute>} />
      <Route path="/join/:roomId" element={<PublicJoinPage />} />
      <Route path="/p/:revisitToken" element={<PublicRevisitPage />} />
      <Route path="/diagnostics/webrtc/:roomId" element={<WebRTCDiagnostics />} />
      {/* Public alias for the Meta Ads tracker — no login required. Other
          internal links still bounce to /login because they sit behind
          ProtectedRoute. */}
      <Route path="/fbads" element={<MetaAdsTracker publicMode />} />

      {/* Public customer-facing landing page — Strategy Session (Feb 2026).
      {/* Public — Strategy Session A/B splitter (static). The bare path
          just forwards to the trailing-slash URL served by the static
          asset host so the splitter's iframe fills the browser window. */}
      <Route path="/strategy-session" element={<StrategySessionRedirect />} />

      {/* Public — Derwent Links (Linktree-style, /DerwentLinks). Case-insensitive. */}
      <Route path="/DerwentLinks" element={<DerwentLinksPage />} />
      <Route path="/derwentlinks" element={<DerwentLinksPage />} />
      <Route path="/links" element={<DerwentLinksPage />} />

      {/* Admin — leads viewer restricted-role landing hub (protected). */}
      <Route path="/leads-viewer-hub" element={<ProtectedRoute><LeadsViewerHub /></ProtectedRoute>} />

      {/* Admin — manage Derwent Links config + analytics (protected). */}
      <Route path="/derwent-links-admin" element={<ProtectedRoute><DerwentLinksAdmin /></ProtectedRoute>} />

      {/* Public — ID Verification customer portal. Accessed via unique token. */}
      <Route path="/verify/:token" element={<IDVerifyCustomerPortal />} />
      <Route path="/verify-start/:intakeToken" element={<IDVerifyStart />} />

      {/* Public — My Derwent Hub customer portal. Accessed via magic-link token. */}
      <Route path="/hub/:token" element={<MyDerwentHub />} />
      {/* Public — Connected Onboarding (fact find) — one secure link per applicant. */}
      <Route path="/onboard/:token" element={<OnboardingPortal />} />
      <Route path="/portal-preview" element={<PortalPreview />} />
      <Route path="/esign/sign/:token" element={<EsignSigner />} />
      <Route path="/activate/:token" element={<ActivateAccount />} />

      {/* Admin — ID Verification portal (protected). */}
      <Route path="/id-verification" element={<ProtectedRoute><IDVerificationAdmin /></ProtectedRoute>} />

      {/* Admin — My Derwent Hub (protected). Broker manages every client hub. */}
      <Route path="/derwent-hub" element={<ProtectedRoute><DerwentHubAdmin /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingWorkbench /></ProtectedRoute>} />
      <Route path="/calling" element={<ProtectedRoute><CallingQueue /></ProtectedRoute>} />
      <Route path="/calling/phone/:token" element={<ProtectedRoute><CallHandoff /></ProtectedRoute>} />
      <Route path="/contacts" element={<ProtectedRoute><Contacts /></ProtectedRoute>} />
      <Route path="/derwentai-reports" element={<ProtectedRoute><DerwentAIReports /></ProtectedRoute>} />
      <Route path="/security" element={<ProtectedRoute><SecurityCentre /></ProtectedRoute>} />
      <Route path="/setup" element={<ProtectedRoute><BrokerSetup /></ProtectedRoute>} />
      <Route path="/system-diagnostics" element={<ProtectedRoute><SystemDiagnostics /></ProtectedRoute>} />
      <Route path="/onboarding/:id" element={<ProtectedRoute><OnboardingDetail /></ProtectedRoute>} />
      <Route path="/fact-find-settings" element={<ProtectedRoute><FactFindSettings /></ProtectedRoute>} />
      <Route path="/client-portal-team-templates" element={<ProtectedRoute><TeamTemplates /></ProtectedRoute>} />
      <Route path="/executive-growth" element={<ProtectedRoute><ExecutiveGrowthTool /></ProtectedRoute>} />
      <Route path="/fact-find-settings/disclosures" element={<ProtectedRoute><DisclosureDocEditor /></ProtectedRoute>} />
      <Route path="/fact-find-settings/:id" element={<ProtectedRoute><FactFindSettings /></ProtectedRoute>} />
      <Route path="/settings/file-readiness-rules" element={<ProtectedRoute><FileReadinessRules /></ProtectedRoute>} />
      <Route path="/esign" element={<Navigate to="/onboarding?pipeline=esign" replace />} />
      <Route path="/esign/document/:docId" element={<ProtectedRoute><EsignDocumentDetail /></ProtectedRoute>} />
      <Route path="/post-settlement" element={<ProtectedRoute><PostSettlementCRM /></ProtectedRoute>} />
      <Route path="/post-settlement/clients/:loan_id" element={<ProtectedRoute><PostSettlementClientProfile /></ProtectedRoute>} />
      
      {/* Protected Routes */}
      <Route path="/" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
      <Route path="/workforce" element={<ProtectedRoute><Workforce /></ProtectedRoute>} />
      <Route path="/my-activity" element={<ProtectedRoute><MyActivity /></ProtectedRoute>} />
      <Route path="/account-setup" element={<ProtectedRoute><AccountSetup /></ProtectedRoute>} />
      <Route path="/settings/users" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
      <Route path="/drop-drive" element={<ProtectedRoute><DropDrive /></ProtectedRoute>} />
      <Route path="/bulk-sms" element={<ProtectedRoute><BulkSms /></ProtectedRoute>} />
      <Route path="/integrations/jarvis" element={<ProtectedRoute><JarvisIntegration /></ProtectedRoute>} />
      <Route path="/jarvis" element={<ProtectedRoute><JarvisChief /></ProtectedRoute>} />
      <Route path="/myweek" element={<ProtectedRoute><MyWeek /></ProtectedRoute>} />
      <Route path="/dropdrive" element={<Navigate to="/drop-drive" replace />} />
      <Route path="/settings/broker-profile" element={<ProtectedRoute><BrokerProfileSettings /></ProtectedRoute>} />
      <Route path="/accelerate-calculator" element={<ProtectedRoute><Calculator /></ProtectedRoute>} />
      <Route path="/lvr-calculator" element={<ProtectedRoute><LVRCalculator /></ProtectedRoute>} />
      <Route path="/home-loan-calculator" element={<ProtectedRoute><HomeLoanCalculator /></ProtectedRoute>} />
      <Route path="/lmi-calculator" element={<ProtectedRoute><LMICalculator /></ProtectedRoute>} />
      <Route path="/lmi-vs-wait-calculator" element={<ProtectedRoute><LMIvsWaitCalculator /></ProtectedRoute>} />
      <Route path="/discharges" element={<ProtectedRoute><DischargesPage /></ProtectedRoute>} />
      <Route path="/construction-cost-calculator" element={<ProtectedRoute><ConstructionCostCalculator /></ProtectedRoute>} />
      <Route path="/activity" element={<Navigate to="/game-plan" replace />} />
      <Route path="/hot-off-the-press" element={<ProtectedRoute><HotOffThePress /></ProtectedRoute>} />
      <Route path="/investor-property-report" element={<ProtectedRoute><InvestorPropertyReport /></ProtectedRoute>} />
      <Route path="/blueprint-breakdown" element={<ProtectedRoute><BlueprintBreakDown /></ProtectedRoute>} />
      <Route path="/hey-derwent" element={<Navigate to={launchedAsApp ? "/" : "/myweek"} replace />} />
      <Route path="/hey-jarvis" element={<Navigate to="/myweek" replace />} />
      {/* Unified workspace — merges To-Do List + Lead Nurture into /work */}
      <Route path="/work" element={<ProtectedRoute><Work /></ProtectedRoute>} />
      <Route path="/todos" element={<Navigate to="/work?view=tasks" replace />} />
      <Route path="/todo-list" element={<Navigate to="/work?view=tasks" replace />} />
      <Route path="/bdm-contacts" element={<ProtectedRoute><BDMContactsPage /></ProtectedRoute>} />
      <Route path="/compliance" element={<ProtectedRoute><CompliancePage /></ProtectedRoute>} />
      <Route path="/notes-ai" element={<ProtectedRoute><NotesAI /></ProtectedRoute>} />
      <Route path="/breakdown-report" element={<ProtectedRoute><BreakDownReport /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/client-management" element={<ProtectedRoute><ClientManagement /></ProtectedRoute>} />
      <Route path="/client-portal-templates" element={<ProtectedRoute><ClientPortalTemplates /></ProtectedRoute>} />
      <Route path="/client-portal-reports" element={<ProtectedRoute><ClientPortalReports /></ProtectedRoute>} />
      <Route path="/game-plan" element={<ProtectedRoute><GamePlanTracker /></ProtectedRoute>} />
      <Route path="/game-plan/tv" element={<ProtectedRoute><GamePlanTV /></ProtectedRoute>} />
      <Route path="/cfo" element={<ProtectedRoute><CFO /></ProtectedRoute>} />
      <Route path="/ceo" element={<ProtectedRoute><CEO /></ProtectedRoute>} />
      <Route path="/lender-packs" element={<ProtectedRoute><LenderSubmissionPacks /></ProtectedRoute>} />
      <Route path="/lead-nurture" element={<ProtectedRoute><LeadNurture /></ProtectedRoute>} />
      <Route path="/lead-nurture/legacy" element={<ProtectedRoute><LeadNurtureLegacy /></ProtectedRoute>} />
      <Route path="/pdf-studio" element={<ProtectedRoute><PdfStudio /></ProtectedRoute>} />
      <Route path="/media-engine" element={<ProtectedRoute><MediaEngine /></ProtectedRoute>} />
      <Route path="/marketing" element={<ProtectedRoute><MarketingCommandCentre /></ProtectedRoute>} />
      <Route path="/meta-ads" element={<ProtectedRoute><MetaAdsTracker /></ProtectedRoute>} />
      <Route path="/pre-built-scripts" element={<ProtectedRoute><PreBuiltScripts /></ProtectedRoute>} />
      <Route path="/scripts" element={<ProtectedRoute><PreBuiltScripts /></ProtectedRoute>} />
      <Route path="/settled-loans" element={<ProtectedRoute><SettledLoans /></ProtectedRoute>} />
      <Route path="/loan-book" element={<ProtectedRoute><SettledLoans /></ProtectedRoute>} />
      <Route path="/lead-nurture/leads/:leadId" element={<ProtectedRoute><LeadDetailPage /></ProtectedRoute>} />
      <Route path="/lead-nurture/:leadId" element={<ProtectedRoute><LeadDetailPage /></ProtectedRoute>} />
      <Route path="/calls-to-text" element={<ProtectedRoute><CallsToText /></ProtectedRoute>} />
      <Route path="/read-my-income" element={<ProtectedRoute><ReadMyIncome /></ProtectedRoute>} />
      <Route path="/meetings" element={<ProtectedRoute><Meetings /></ProtectedRoute>} />
      <Route path="/meeting/:meetingId" element={<ProtectedRoute><BrokerMeetingPage /></ProtectedRoute>} />
      
      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </WorkforceProvider>
    </WarmThemeProvider>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors closeButton />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
